import { router, useForm } from "@inertiajs/react"
import { type FormEvent, useEffect, useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { flushListCache, flushListsIndexCache } from "@/lib/cache"
import { listTaskExtensionPath, listTaskPath } from "@/routes"
import type { Task } from "@/types"

type EditableTask = Pick<Task, "id" | "description" | "death_date" | "status">

interface EditTaskModalProps {
  task: EditableTask | null
  onClose: () => void
  listId: number
  autoArchiveDays?: number
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00")
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function addDays(dateStr: string, days: number): string {
  const date = new Date(dateStr + "T00:00:00")
  date.setDate(date.getDate() + days)
  return date.toISOString().split("T")[0]
}

export function EditTaskModal({
  task,
  onClose,
  listId,
  autoArchiveDays = 7,
}: EditTaskModalProps) {
  const form = useForm({
    task: {
      description: "",
    },
  })

  const [selectedExtension, setSelectedExtension] = useState<string>("")

  const extensionOptions = useMemo(() => {
    if (!task?.death_date) return []

    const options = [1, 3, 7]
      .filter((days) => days >= autoArchiveDays)
      .slice(0, 3)

    if (options.length === 0) {
      options.push(autoArchiveDays, autoArchiveDays * 2, autoArchiveDays * 3)
    } else if (options.length < 3) {
      const existingSet = new Set(options)
      const candidates = [1, 3, 7, 14].filter((d) => !existingSet.has(d))
      while (options.length < 3 && candidates.length > 0) {
        options.push(candidates.shift()!)
      }
      options.sort((a, b) => a - b)
    }

    return options.map((days) => ({
      days,
      targetDate: addDays(task.death_date!, days),
      label: `${formatDateLabel(addDays(task.death_date!, days))} (+${days})`,
    }))
  }, [task?.death_date, autoArchiveDays])

  const { setData } = form

  useEffect(() => {
    if (task) {
      setData({
        task: {
          description: task.description,
        },
      })
      setSelectedExtension("")
    }
    // We intentionally only sync when task identity changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!task) return

    const flushCache = () => {
      flushListCache(listId)
      flushListsIndexCache()
    }

    const finalize = () => {
      form.reset()
      setSelectedExtension("")
      onClose()
    }

    if (selectedExtension) {
      const days = parseInt(selectedExtension, 10)
      router.post(
        listTaskExtensionPath(listId, task.id),
        { days },
        {
          preserveScroll: true,
          onSuccess: () => {
            flushCache()
            form.patch(listTaskPath(listId, task.id), {
              preserveScroll: true,
              onSuccess: finalize,
            })
          },
        },
      )
    } else {
      form.patch(listTaskPath(listId, task.id), {
        preserveScroll: true,
        onSuccess: () => {
          flushCache()
          finalize()
        },
      })
    }
  }

  return (
    <Dialog open={!!task} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>EDIT TASK</DialogTitle>
            <DialogDescription>
              Update task details while it still draws breath
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label
                htmlFor="edit-description"
                className="font-mono text-xs tracking-wider uppercase"
              >
                Description
              </Label>
              <Input
                id="edit-description"
                value={form.data.task.description}
                onChange={(e) =>
                  form.setData("task", {
                    ...form.data.task,
                    description: e.target.value,
                  })
                }
                placeholder="Task description..."
                autoComplete="off"
                required
              />
            </div>

            {task?.status === "active" &&
              task?.death_date &&
              extensionOptions.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-mono text-xs tracking-wider uppercase">
                    Extend Deadline
                    <span className="text-muted-foreground ml-2 font-normal normal-case">
                      (Dies: {formatDateLabel(task.death_date)})
                    </span>
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={selectedExtension}
                    onValueChange={setSelectedExtension}
                    className="justify-start"
                    variant="outline"
                  >
                    {extensionOptions.map((option) => (
                      <ToggleGroupItem
                        key={option.days}
                        value={String(option.days)}
                        className="text-xs"
                      >
                        {option.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>
              )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={form.processing}>
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
